import React from 'react';
import { ShoppingCart, User, Menu, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useShop } from '../../context/ShopContext';
import { Link, useNavigate } from 'react-router-dom';

const Navbar = () => {
  const { openCart, cartCount } = useShop();
  const navigate = useNavigate();

  return (
    <div className="w-full">
      {/* Top Bar - Announcements */}
      <div className="bg-primary text-primary-foreground py-2 px-4 text-center text-xs md:text-sm font-medium tracking-wide">
        <span>🌿 Åpent Man-Fre 10-19, Lør 10-18, Søn 12-18 | 📍 Kaj Munks vei 41, Oslo</span>
      </div>

      {/* Main Header */}
      <header className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50 transition-all shadow-sm">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between gap-4 md:gap-8">
            
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[400px]">
        <nav className="flex flex-col gap-4 mt-8">
                  <SheetClose asChild><Link to="/category/blomster" className="text-lg font-medium hover:text-primary">Blomsterfrø</Link></SheetClose>
                  <SheetClose asChild><Link to="/category/gronnsaker" className="text-lg font-medium hover:text-primary">Grønnsaker</Link></SheetClose>
                  <SheetClose asChild><Link to="/category/krydderurter" className="text-lg font-medium hover:text-primary">Krydderurter</Link></SheetClose>
                  <SheetClose asChild><Link to="/category/okologisk-dyrkning" className="text-lg font-medium hover:text-primary">Økologisk dyrkning</Link></SheetClose>
                  <SheetClose asChild><Link to="/category/bie-og-insektpleie" className="text-lg font-medium hover:text-primary">Bie & Insektpleie</Link></SheetClose>
                  <SheetClose asChild><Link to="/category/naturgjodsel" className="text-lg font-medium hover:text-primary">Naturgjødsel</Link></SheetClose>
                  <hr className="my-2" />
                  <SheetClose asChild><Link to="/category/alle" className="text-lg font-medium hover:text-primary">Alle produkter</Link></SheetClose>
                  <hr className="my-2" />
                  <SheetClose asChild><Link to="/kundeservice" className="flex items-center gap-2 text-sm text-muted-foreground"><User className="h-4 w-4" /> Kundeservice</Link></SheetClose>
                  <SheetClose asChild><Link to="/kontakt" className="flex items-center gap-2 text-sm text-muted-foreground">Kontakt oss</Link></SheetClose>
                </nav>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex flex-col items-center md:items-start shrink-0 group">
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-primary tracking-tight leading-none group-hover:text-primary/90 transition-colors">
                Norsk Frøhandel
              </h1>
              <span className="text-[10px] md:text-xs tracking-widest text-muted-foreground uppercase hidden md:block group-hover:text-accent transition-colors">
                Kvalitet & Kunnskap
              </span>
            </Link>

            {/* Spacer */}
            <div className="hidden md:flex flex-1" />

            {/* Actions */}
            <div className="flex items-center gap-2 md:gap-4">
               <Link to="/kontakt">
                 <Button variant="ghost" size="icon" className="hidden md:flex text-muted-foreground hover:text-primary" title="Veibeskrivelse">
                  <MapPin className="h-5 w-5" />
                </Button>
               </Link>
               <Link to="/kundeservice">
                 <Button variant="ghost" size="icon" className="hidden md:flex text-muted-foreground hover:text-primary" title="Min Side">
                  <User className="h-5 w-5" />
                </Button>
               </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-primary hover:bg-primary/10 relative"
                onClick={openCart}
                title="Handlekurv"
              >
                <ShoppingCart className="h-6 w-6" />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-accent text-white rounded-full text-[10px] animate-in zoom-in duration-300">
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
          
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:block border-t">
          <div className="container mx-auto px-4">
            <nav className="flex items-center justify-center gap-8 py-3">
              <Link to="/category/blomster" className="text-sm font-medium hover:text-primary transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full">Blomsterfrø</Link>
              <Link to="/category/gronnsaker" className="text-sm font-medium hover:text-primary transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full">Grønnsaker</Link>
              <Link to="/category/krydderurter" className="text-sm font-medium hover:text-primary transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full">Krydderurter</Link>
              <Link to="/category/okologisk-dyrkning" className="text-sm font-medium hover:text-primary transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full">Økologisk dyrkning</Link>
              <Link to="/category/bie-og-insektpleie" className="text-sm font-medium hover:text-primary transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full">Bie & Insektpleie</Link>
              <Link to="/category/naturgjodsel" className="text-sm font-medium text-accent hover:text-accent/80 transition-colors font-bold">Naturgjødsel</Link>
            </nav>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Navbar;
